import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Navigate } from "react-router-dom";
import { usePage } from "@/contexts/PageContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OrderQuantitySelector } from "./checkout/OrderQuantitySelector";
import { getContrastTextColor } from "@/utils/colorUtils";
import { formatCentsAsUsd } from "@/lib/money";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { browserCanGoBack } from "@/utils/browserCanGoBack";

interface DisplayProduct {
  _id: string;
  name?: string;
  variantName?: string;
  /** Markdown; same as checkout line items */
  description?: string;
  price: number;
  images?: { url: string; thumbnailUrl?: string }[];
  payWhatYouCan?: boolean;
  quantityRules?: { minimum: number; maximum: number };
}

function ReceiptHeader({
  textColor,
  title,
  canGoBack: showBack,
}: {
  textColor: string;
  title: string;
  canGoBack: boolean;
}) {
  return (
    <div className="relative flex items-center justify-center min-h-10">
      {showBack ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            "absolute left-0 shrink-0 h-10 w-10",
            textColor === "white"
              ? "text-white hover:bg-white/15 hover:text-white focus-visible:ring-white/35"
              : "text-black hover:bg-black/10 hover:text-black focus-visible:ring-black/25"
          )}
          aria-label="Go back"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      ) : null}
      <h1 className="text-2xl font-bold text-center">{title}</h1>
    </div>
  );
}

export function OrderReceiptRoute() {
  const { id: orderId } = useParams<{ id: string }>();
  const { setCheckoutSettings } = usePage();
  const [canGoBack] = useState(browserCanGoBack);
  const [artistName, setArtistName] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>();
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [products, setProducts] = useState<DisplayProduct[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [receiptTip, setReceiptTip] = useState(false);
  const [checkoutSettings, setLocalCheckoutSettings] = useState<{
    colors?: {
      backgroundColor?: string;
      buttonColor?: string;
      accentColor?: string;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [shippingChargedOption, setShippingChargedOption] = useState<{
    name: string;
    cost: number;
  } | null>(null);
  const [resumeHostedCheckout, setResumeHostedCheckout] = useState<{
    checkoutId: string;
    step: "contact" | "shipping" | "payment";
  } | null>(null);
  const [receiptFooterHtml, setReceiptFooterHtml] = useState<string | undefined>();

  const noopQuantity = useCallback(() => {}, []);

  useEffect(() => {
    if (!orderId) return;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setReceiptTip(false);
      setReceiptFooterHtml(undefined);
      setShippingChargedOption(null);
      setResumeHostedCheckout(null);
      try {
        const res = await fetch(`/api/orders/${orderId}`);
        if (!res.ok) {
          if (res.status === 404) setNotFound(true);
          return;
        }
        const data = (await res.json()) as {
          artistName?: string;
          payment?: { status?: string; amount?: number };
          products?: DisplayProduct[];
          quantities?: Record<string, number>;
          receipt?: {
            tip?: boolean;
            shipping?: { chargedOption?: { name?: string; cost?: number } };
            receiptFooterHtml?: string;
          };
          checkoutSettings?: {
            colors?: {
              backgroundColor?: string;
              buttonColor?: string;
              accentColor?: string;
            };
          } | null;
          resumeHostedCheckout?: {
            checkoutId: string;
            step: "contact" | "shipping" | "payment";
          };
        };
        if (data.resumeHostedCheckout) {
          setResumeHostedCheckout(data.resumeHostedCheckout);
          setLoading(false);
          return;
        }
        setArtistName(data.artistName ?? "");
        setPaymentStatus(data.payment?.status);
        setPaymentAmount(data.payment?.amount ?? 0);
        setProducts(data.products ?? []);
        setQuantities(data.quantities ?? {});
        setReceiptTip(data.receipt?.tip === true);
        setReceiptFooterHtml(data.receipt?.receiptFooterHtml);
        const co = data.receipt?.shipping?.chargedOption;
        if (
          co &&
          typeof co.cost === "number" &&
          co.cost > 0 &&
          typeof co.name === "string" &&
          co.name.trim().length > 0
        ) {
          setShippingChargedOption({ name: co.name.trim(), cost: Math.round(co.cost) });
        } else {
          setShippingChargedOption(null);
        }
        const cs = data.checkoutSettings ?? null;
        setLocalCheckoutSettings(cs);
        if (cs) setCheckoutSettings(cs);
      } catch (e) {
        console.error("Order receipt fetch error:", e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [orderId, setCheckoutSettings]);

  const backgroundColor = checkoutSettings?.colors?.backgroundColor ?? "#ffffff";
  const textColor = getContrastTextColor(backgroundColor);

  const receiptTitle = (() => {
    if (!orderId) return "Order";
    if (notFound) return "Order not found";
    const titleBase = artistName.trim() ? artistName.trim() : "Receipt";
    return paymentStatus === "complete"
      ? `Receipt · ${titleBase}`
      : `Order · ${titleBase}`;
  })();

  const handleCancel = async () => {
    if (!orderId) return;
    setCancelError(null);
    setCancelling(true);
    try {
      const res = await fetch(`/api/payments/${orderId}`, { method: "POST" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCancelError(typeof body.error === "string" ? body.error : "Cancel failed");
        return;
      }
      setPaymentStatus("cancelled");
      setCancelDialogOpen(false);
      toast.success("Your order has been cancelled.");
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  if (!orderId) {
    return (
      <>
        <Helmet>
          <title>{receiptTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center" style={{ color: textColor }}>
          Missing order ID
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{receiptTitle}</title>
        </Helmet>
        <div className="min-h-screen w-full" style={{ backgroundColor }}>
          <div
            className="max-w-4xl w-full mx-auto min-h-screen flex flex-col py-4 px-4"
            style={{ color: textColor }}
          >
            <ReceiptHeader textColor={textColor} title="Receipt" canGoBack={canGoBack} />
            <div className="flex-1 flex items-center justify-center">Loading...</div>
          </div>
        </div>
      </>
    );
  }

  if (resumeHostedCheckout) {
    return (
      <>
        <Helmet>
          <title>Checkout</title>
        </Helmet>
        <Navigate
          to={`/checkout/${resumeHostedCheckout.checkoutId}/${resumeHostedCheckout.step}`}
          replace
        />
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <Helmet>
          <title>{receiptTitle}</title>
        </Helmet>
        <div className="min-h-screen w-full" style={{ backgroundColor }}>
          <div
            className="max-w-4xl w-full mx-auto min-h-screen flex flex-col py-4 px-4"
            style={{ color: textColor }}
          >
            <ReceiptHeader textColor={textColor} title="Receipt" canGoBack={canGoBack} />
            <div className="flex-1 flex items-center justify-center">Order not found</div>
          </div>
        </div>
      </>
    );
  }

  const hasTipLine = products.some((p) => (p.name ?? "").trim().toLowerCase() === "tip");

  const lineTotalCents = ({
    prods,
    qty,
  }: {
    prods: DisplayProduct[];
    qty: Record<string, number>;
  }) =>
    prods.reduce(
      (acc, p) => acc + Math.round(p.price ?? 0) * (qty[p._id] ?? 1),
      0
    );

  const opt = shippingChargedOption;
  const matchIdx =
    opt != null
      ? products.findIndex(
          (p) =>
            (p.name ?? "").trim() === opt.name &&
            Math.round(p.price ?? 0) === opt.cost
        )
      : -1;
  const shippingRowPulled =
    opt != null && matchIdx !== -1 ? products[matchIdx]! : null;
  const receiptProducts =
    shippingRowPulled != null
      ? products.filter((_, i) => i !== matchIdx)
      : products;
  const receiptQuantities = (() => {
    if (shippingRowPulled == null) return quantities;
    const next = { ...quantities };
    delete next[shippingRowPulled._id];
    return next;
  })();
  const receiptShippingCents = shippingRowPulled != null && opt != null ? opt.cost : 0;
  const receiptShippingLabel =
    receiptShippingCents > 0 && opt != null ? opt.name : undefined;
  const receiptLineSubtotalCents = lineTotalCents({
    prods: receiptProducts,
    qty: receiptQuantities,
  });

  return (
    <>
      <Helmet>
        <title>{receiptTitle}</title>
      </Helmet>
      <div className="min-h-screen w-full" style={{ backgroundColor }}>
      <div
        className="max-w-4xl w-full mx-auto min-h-screen flex flex-col py-4 px-4"
        style={{ color: textColor }}
      >
        <ReceiptHeader textColor={textColor} title="Receipt" canGoBack={canGoBack} />

        <div className="flex-1 mt-2 space-y-6">
          {products.length > 0 ? (
            <OrderQuantitySelector
              products={receiptProducts}
              quantities={receiptQuantities}
              onQuantityChange={noopQuantity}
              total={receiptLineSubtotalCents}
              shippingCents={receiptShippingCents}
              shippingLabel={receiptShippingLabel}
              readOnly
              checkoutColors={checkoutSettings?.colors ?? null}
              showShippingProtectionLineItem={false}
              tipAmount={0}
            />
          ) : (
            <div className="text-center text-sm opacity-80 py-8 space-y-2">
              <p>No line items recorded for this order.</p>
              {paymentAmount > 0 ? (
                <p className="font-semibold text-lg">Total: ${formatCentsAsUsd(paymentAmount)}</p>
              ) : null}
            </div>
          )}

          {receiptTip && !hasTipLine ? (
            <p className="text-sm text-center opacity-80">Thank you — this order included a tip.</p>
          ) : null}

          {paymentStatus === "uncaptured" ? (
            <>
              <div className="mt-4 flex justify-center">
                <Button
                  type="button"
                  className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
                  disabled={cancelling}
                  onClick={() => {
                    setCancelError(null);
                    setCancelDialogOpen(true);
                  }}
                >
                  Cancel
                </Button>
              </div>
              <Dialog
                open={cancelDialogOpen}
                onOpenChange={(open) => {
                  if (cancelling) return;
                  setCancelDialogOpen(open);
                  if (!open) setCancelError(null);
                }}
              >
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Cancel this order?</DialogTitle>
                    <DialogDescription>
                      Your held payment will be released and this order will not be completed. You can
                      place a new order later if you change your mind.
                    </DialogDescription>
                  </DialogHeader>
                  {cancelError ? (
                    <p className="text-sm text-red-600 dark:text-red-400">{cancelError}</p>
                  ) : null}
                  <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={cancelling}
                      onClick={() => setCancelDialogOpen(false)}
                    >
                      Keep order
                    </Button>
                    <Button
                      type="button"
                      className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
                      disabled={cancelling}
                      onClick={() => void handleCancel()}
                    >
                      {cancelling ? "Cancelling..." : "Yes, cancel order"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          ) : null}
        </div>

        {receiptFooterHtml ? (
          <div
            className="mt-8 text-center text-sm receipt-footer-html"
            dangerouslySetInnerHTML={{ __html: receiptFooterHtml }}
          />
        ) : null}
      </div>
    </div>
    </>
  );
}
